import type { UserGetAppData } from '../../../../@api-client';
import type { OfflineStorage, OfflineStorageScope } from '../storage/offline-storage';

const BOOTSTRAP_KEY = 'active';
const BOOTSTRAP_VENUE_ID = '__bootstrap__';

/** Bump when `UserGetAppData` gains an authoritative field, so older snapshots are discarded. */
export const OFFLINE_BOOTSTRAP_SCHEMA_VERSION = 2;

export type OfflineBootstrapSnapshot = {
  firebaseUserId: string;
  savedAt: string;
  schemaVersion: number;
  user: UserGetAppData;
};

export type OfflineBootstrapStore = {
  clear: (userId: string) => Promise<void>;
  get: (userId: string) => Promise<OfflineBootstrapSnapshot | undefined>;
  save: (
    userId: string,
    snapshot: Omit<OfflineBootstrapSnapshot, 'schemaVersion'>,
  ) => Promise<void>;
};

const getBootstrapScope = (userId: string): OfflineStorageScope => ({
  userId,
  venueId: BOOTSTRAP_VENUE_ID,
});

export const createOfflineBootstrapStore = (storage: OfflineStorage): OfflineBootstrapStore => ({
  async clear(userId) {
    await storage.delete(getBootstrapScope(userId), 'session', BOOTSTRAP_KEY);
  },
  async get(userId) {
    const snapshot = await storage.get<OfflineBootstrapSnapshot>(
      getBootstrapScope(userId),
      'session',
      BOOTSTRAP_KEY,
    );
    if (snapshot?.firebaseUserId !== userId) return undefined;

    return snapshot.schemaVersion === OFFLINE_BOOTSTRAP_SCHEMA_VERSION ? snapshot : undefined;
  },
  async save(userId, snapshot) {
    if (snapshot.firebaseUserId !== userId) {
      throw new Error('Offline bootstrap user does not match the authenticated identity.');
    }

    await storage.put(getBootstrapScope(userId), 'session', BOOTSTRAP_KEY, {
      ...snapshot,
      schemaVersion: OFFLINE_BOOTSTRAP_SCHEMA_VERSION,
    });
  },
});
